<template>
  <v-dialog v-model="showReassignTaskModal" persistent width="50vw">
    <v-card>
      <v-card-title class="pt-0 mt-0">
        <h1 class="agent-title">Reassign Task Ownership</h1>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="reassignError" type="error" class="alert-message">
          {{ reassignError }}
        </v-alert>
        <div>
          <p>Select a user from the list below to reassign task ownership and guidance information. Click 'Save' to confirm.</p>
          <p>
            {{ getTotalTaskCountText() }}<br>
            {{ getTotalSessionCountText() }}
          </p>
          <v-radio-group v-model="newProfile" style="height: 50vh; max-height: 50vh; overflow-y: auto;">
            <v-radio
              v-for="user in sortedUsers"
              :key="user.id"
              :label="user.name"
              :value="user.id"
              class="profile-radio"
            ></v-radio>
          </v-radio-group>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="reassignTask()"
        >
          Reassign
        </v-btn>
        <v-btn color="primary" @click="hideModal(false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

export default {
  name: 'ReassignTaskOwnership',
  props: {
    // prop to control the visibility of the modal
    showReassignTaskModal: {
      type: Boolean,
      default: false
    },
    profileId: {
      type: Number,
      default: null
    },
    profileType: {
      type: Number,
      default: null
    },
    users: {
      type: Array,
      default: () => []
    },
    // prop to pass the task and guidance session counts for the profile
    // this should be an object with keys 'task_count' and 'guidance_count'
    // e.g. { task_count: 10, guidance_count: 5 }
    // this will be used to display the total task and session counts in the modal
    profileTaskAndGuidanceSessionCounts: {
      type: Object,
      default: () => ({})
    },
  },
  data() {
    return {
      reassignError: null, // to store any error messages
      newProfile: null, // to store the new profile data
    };
  },
  methods: {
    ...mapActions("taskSummaryData", ["reassignTaskOwnership"]),
    reassignTask() {
      // check if a new profile is selected
      if (!this.newProfile) {
        this.reassignError = "Please select a user to reassign task ownership."
        return
      }
      // call the reassignTaskOwnership action with the old and new user IDs
      this.reassignTaskOwnership({
        old_user_id: this.profileId,
        new_user_id: this.newProfile
      }).then(() => {
        this.$nextTick(() => {
          this.reassignError = null // reset error state
          this.hideModal(true) // hide the modal and reload the data
        })
      }).catch(() => {
        this.reassignError = "Failed to initiate task ownership reassignment."
      })
    },
    // function to hide the modal and reset the state
    hideModal(reload) {
      // reset the newProfile to null
      this.newProfile = null
      // emit an event to hide the modal
      this.$emit("HIDE_REASSIGN_TASK_MODAL", { reload: reload })
    },
    getTotalTaskCountText() {
      // get the total task count from the profileTaskAndGuidanceSessionCounts
      const totalTasks = this.profileTaskAndGuidanceSessionCounts && this.profileTaskAndGuidanceSessionCounts.tasks_count ? this.profileTaskAndGuidanceSessionCounts.tasks_count : 0;
      return `Total Tasks: ${totalTasks}`;
    },
    getTotalSessionCountText() {
      // get the total session count from the profileTaskAndGuidanceSessionCounts
      const totalSessions = this.profileTaskAndGuidanceSessionCounts && this.profileTaskAndGuidanceSessionCounts.guidance_count ? this.profileTaskAndGuidanceSessionCounts.guidance_count : 0;
      return `Total Sessions: ${totalSessions}`;
    },
  },
  computed: {
    ...mapGetters("taskSummaryData", [
      "retrieveTaskOwnershipLoadingError",
      "retrieveTaskOwnershipLoadingErrorStatus",
    ]),
    sortedUsers() {
      // sort the users by name in ascending order, and remove oldProfile from the list
      const filteredUsers = this.users.filter(user => user.id !== this.profileId);
      return filteredUsers.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
    this.reassignError = null // reset error state on mount
  },
};
</script>

<style>
.profile-radio label {
  opacity: 1; /* ensure checkbox label is fully opaque */
}
</style>
